"use client"

export const TENANT_BASE_PATH = "/tenant"

const PUBLIC_ROUTES = new Set([
  "/",
  "/about",
  "/accept-invitation",
  "/berger",
  "/branches",
  "/contact",
  "/create-church",
  "/donation",
  "/form-builder",
  "/pricing",
  "/programs",
  "/prayer-request",
  "/register-church",
])

const AUTH_ROUTES = new Set(["/login", "/register"])
const PERSONAL_AUTH_ROUTES = new Set(["/profile", "/workspaces"])
export const AUTH_FAILURE_PATH = "/auth/failure"

export function normalizeTenantPathname(pathname: string) {
  if (!pathname || pathname === TENANT_BASE_PATH) return "/"
  if (pathname.startsWith(`${TENANT_BASE_PATH}/`)) {
    return pathname.slice(TENANT_BASE_PATH.length) || "/"
  }
  return pathname
}

export function isPublicAppRoute(pathname: string) {
  const normalized = normalizeTenantPathname(pathname)
  if (PUBLIC_ROUTES.has(normalized)) return true
  for (const route of PUBLIC_ROUTES) {
    if (route !== "/" && normalized.startsWith(`${route}/`)) {
      return true
    }
  }
  return false
}

export function isAuthAppRoute(pathname: string) {
  const normalized = normalizeTenantPathname(pathname)
  return (
    AUTH_ROUTES.has(normalized) ||
    normalized === AUTH_FAILURE_PATH ||
    normalized.startsWith("/login/") ||
    normalized.startsWith("/register/")
  )
}

export function isPersonalAuthenticatedAppRoute(pathname: string) {
  const normalized = normalizeTenantPathname(pathname)
  if (PERSONAL_AUTH_ROUTES.has(normalized)) return true
  for (const route of PERSONAL_AUTH_ROUTES) {
    if (normalized.startsWith(`${route}/`)) {
      return true
    }
  }
  return false
}

export function isProtectedAppRoute(pathname: string) {
  return !isPublicAppRoute(pathname) && !isAuthAppRoute(pathname)
}

export function buildLoginRedirectPath(pathname: string, search: string) {
  const normalizedPathname = normalizeTenantPathname(pathname)
  const nextPath = `${normalizedPathname}${search}`
  const normalized = nextPath.trim()
  const isLoginPath =
    normalized === "/login" ||
    normalized.startsWith("/login/") ||
    normalized.startsWith("/login?")
  if (!normalized || isLoginPath) return "/login"
  return `/login?next=${encodeURIComponent(normalized)}`
}

export function buildTenantBrowserLoginUrl(pathname: string, search: string) {
  return buildLoginRedirectPath(pathname, search)
}

function decodeNextTarget(value: string) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function collapseAuthFailureNextTarget(next: string | null, fallback = "/dashboard") {
  if (!next) return fallback

  const seen = new Set<string>()
  let current = decodeNextTarget(next).trim()

  for (let i = 0; i < 10; i += 1) {
    if (!current || seen.has(current)) break
    seen.add(current)

    const [pathname, query = ""] = current.split("?")
    if (normalizeTenantPathname(pathname) !== AUTH_FAILURE_PATH) {
      return current.startsWith("/") && !current.startsWith("//") ? current : fallback
    }

    const nextTarget = new URLSearchParams(query).get("next")
    if (!nextTarget) break
    current = decodeNextTarget(nextTarget).trim()
  }

  return fallback
}
